import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ProjectsService } from '../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { changeInWorkRequestListInfo, projectDetails } from '../../../../../../core/projects.selectors';
import { takeUntil } from 'rxjs/operators';
import {
  DeleteCIWRRequest,
  FetchChangeInWorkRequestListRequest,
  SendChangeInWorkRequestReminderRequest,
} from '../../../../../../core/projects.actions';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../shared/components';
import {
  ChangeInWorkRequestSideNavWrapperComponent,
} from '../change-in-work-request-side-nav-wrapper/change-in-work-request-side-nav-wrapper.component';
import * as moment from 'moment/moment';
import { ChangeInWorkRequestService } from '../change-in-work-request.service';
import { AuthService } from '../../../../../../../../shared/services';

@Component({
    selector: 'app-change-in-work-request-list',
    templateUrl: './change-in-work-request-list.component.html',
    styleUrls: ['./change-in-work-request-list.component.scss'],
    standalone: false
})
export class ChangeInWorkRequestListComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['requestNo', 'amountChange', 'workDayChange', 'status', 'actions'];
  dataSource: any = [];
  projectId: string;
  pagination = {
    pageIndex: 0,
    length: 0,
    pageSize: 10,
  };
  currentPage = 1;
  totalRecords: number | undefined;
  file: any;
  selectedRow: any;
  qp: any;
  itemsPerPage = 10;
  originalDataSource = [];
  filteredDataSource: any = [];
  statusMap = {
    submitted: {
      label: 'Awaiting approval',
      bgColor: 'bg-submitted',
      textColor: 'text-submitted',
    },
    approved: {
      label: 'Approved',
      bgColor: 'bg-approved',
      textColor: 'txt-approved',
    },
    rejected: {
      label: 'Rejected',
      bgColor: 'bg-rejected',
      textColor: 'txt-rejected',
    },
    draft: {
      label: 'Draft',
      bgColor: 'bg-draft',
      textColor: 'txt-draft',
    },
  };
  startDate = null;
  endDate = null;
  statusIdsToFilter: any[] = [];
  lastQpSignature: string | null = null;
  searchText = '';
  selectedSortColumn: string | null = 'Request #';
  sortDirection: 'asc' | 'desc' = 'asc';
  currentUser: any;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private changeOrdersRequestService: ChangeInWorkRequestService,
    private authService: AuthService,
  ) {
    this.currentUser = this.authService.getCurrentUser();
  }

  ngOnInit() {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        // Row clicks merge selection params (ciwr, snSubTab) into the URL; only
        // react when the params this list actually depends on change, so
        // selecting a row doesn't refetch and yank the user back to page 1.
        const signature = JSON.stringify({
          subTab: qp['subTab'],
          startDate: qp.startDate || null,
          endDate: qp.endDate || null,
          status: qp.status || null,
        });
        if (signature === this.lastQpSignature) {
          return;
        }
        this.lastQpSignature = signature;

        if (qp['subTab'] === 'ciwr') {
          this.fetchChangeInWorkRequestList();
        }

        if (qp.startDate && moment(qp.startDate).isValid()) {
          this.startDate = qp.startDate;
        } else {
          this.startDate = null;
        }

        if (qp.endDate && moment(qp.endDate).isValid()) {
          this.endDate = qp.endDate;
        } else {
          this.endDate = null;
        }

        if (qp.status) {
          if (Array.isArray(qp.status)) {
            this.statusIdsToFilter = qp.status;
          } else {
            this.statusIdsToFilter = [qp.status];
          }
        } else {
          this.statusIdsToFilter = [];
        }

        this.applyFilters();
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.projectId = project?.id;
        this.fetchChangeInWorkRequestList();
      });

    this.store.select(changeInWorkRequestListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          data = JSON.parse(JSON.stringify(data));

          // Sync only real responses (`loaded` is stamped by the fetch effect),
          // INCLUDING empty ones — the old `list.length > 0` guard meant a
          // delete that emptied the results never cleared the table. The
          // guard also keeps the initial empty store state from blanking the
          // table. (TODO: the reducer's reset-on-request spreads the initial
          // info object into the TOP-LEVEL state instead of resetting
          // changeInWorkRequestListInfo — pre-existing bug shared by several
          // reducers; harmless here but worth a cleanup pass.)
          if (data?.loaded) {
            this.originalDataSource = data?.list || [];
            this.totalRecords = data?.totalRecords || 0;

            // Data refresh (e.g. after a workflow action) — keep the page the
            // user is on; only explicit filter changes reset to page 1.
            this.applyFilters(false);
          }
        }
      });

    this.commonService.clearRowSelection
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.selectedRow = null;
      });

    this.changeOrdersRequestService.search
      .pipe(takeUntil(this.onDestroy))
      .subscribe((text: string) => {
        this.searchText = text || '';
        this.applyFilters();
      });

    this.changeOrdersRequestService.sort
      .pipe(takeUntil(this.onDestroy))
      .subscribe((sort) => {
        this.selectedSortColumn = sort.column;
        this.sortDirection = sort.direction;
        this.applyFilters();
      });
  }

  rowClicked(data) {
    this.selectedRow = data;

    this.projectsService.openRightPanel.next({
      component: ChangeInWorkRequestSideNavWrapperComponent,
      data: {
        ...data,
        createRequest: false,
      },
    });

    this.router.navigate([], {
      queryParams: {
        subTab: 'ciwr',
        snSubTab: 'details',
        ciwr: data.id,
      },
      queryParamsHandling: 'merge',
    });
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;

    this.paginateData();
  }

  fetchChangeInWorkRequestList() {
    // Pagination is client-side (paginateData), so always fetch the full list.
    // Offsetting by pageIndex here made any refetch triggered while on page 2+
    // (e.g. the query-param change from clicking a row) load only the tail of
    // the list, collapsing the paginator to a single page.
    const payload = {
      projectId: this.projectId,
      qp: {
        start: 0,
        total: 1000,
      },
    };

    if (payload.projectId) {
      this.store.dispatch(FetchChangeInWorkRequestListRequest({ payload }));
    }
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    } else {
      const maxPage = Math.max(1, Math.ceil((this.filteredDataSource?.length || 0) / this.itemsPerPage));
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage;
      }
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredDataSource?.length);

    this.dataSource = this.filteredDataSource.slice(startIndex, endIndex).sort((a, b) => {
      return a.item - b.item;
    });
  }

  applyFilters(resetPage = true) {
    let filteredData = [...this.originalDataSource];

    if (this.startDate) {
      const start = moment(this.startDate, 'YYYY-MM-DD');
      filteredData = filteredData.filter(data =>
        moment(data.created_at, 'YYYY-MM-DD').isSameOrAfter(start),
      );
    }

    if (this.endDate) {
      const end = moment(this.endDate, 'YYYY-MM-DD');
      filteredData = filteredData.filter(data =>
        moment(data.created_at, 'YYYY-MM-DD').isSameOrBefore(end),
      );
    }

    if (this.statusIdsToFilter?.length) {
      filteredData = filteredData.filter(data =>
        this.statusIdsToFilter.some(status => this.matchesStatusFilter(data, status)),
      );
    }

    if (this.searchText.trim()) {
      const search = this.searchText.trim().toLowerCase();
      filteredData = filteredData.filter(data =>
        (data.description?.toLowerCase().includes(search) ||
          data.request_no?.toLowerCase().includes(search)),
      );
    }

    filteredData = this.sortItems(filteredData);

    this.filteredDataSource = filteredData;
    this.totalRecords = filteredData.length;
    this.paginateData(resetPage);
  }

  /**
   * Supports the approved sub-filters: approved requests already combined into
   * a change order vs approved ones still available.
   */
  private matchesStatusFilter(row: any, status: string): boolean {
    if (status === 'approved-in-co') {
      return row.status === 'approved' && !!row.linked_change_order_id;
    }
    if (status === 'approved-not-in-co') {
      return row.status === 'approved' && !row.linked_change_order_id;
    }
    return row.status === status;
  }

  toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.applyFilters();
  }

  private sortItems(items: any[]): any[] {
    const itemsToSort = [...items];
    const safeDate = (d: any) => (d ? new Date(d).getTime() : 0);

    if (!this.selectedSortColumn) {
      return itemsToSort.sort((a, b) => safeDate(b.updated_at) - safeDate(a.updated_at));
    }

    const sortFns: Record<string, (a: any, b: any) => number> = {
      'Request #': (a, b) => (a.request_no || '').localeCompare(b.request_no || '', undefined, { numeric: true }),
      'Amount change': (a, b) => (parseFloat(a.amount_change) || 0) - (parseFloat(b.amount_change) || 0),
      'Work days change': (a, b) => (parseFloat(a.work_day_change) || 0) - (parseFloat(b.work_day_change) || 0),
      'Status': (a, b) => (a.status || '').localeCompare(b.status || ''),
      'Created at': (a, b) => safeDate(a.created_at) - safeDate(b.created_at),
    };

    const fn = sortFns[this.selectedSortColumn];
    if (!fn) {
      return itemsToSort.sort((a, b) => safeDate(b.updated_at) - safeDate(a.updated_at));
    }

    const applyDirection = (result: number) => (this.sortDirection === 'asc' ? result : -result);
    return itemsToSort.sort((a, b) => applyDirection(fn(a, b)));
  }

  isCreator(row: any): boolean {
    if (!row?.createdBy || !this.currentUser?.id) {
      return false;
    }
    return row.createdBy === this.currentUser.id || row.createdBy?.id === this.currentUser.id;
  }

  shouldShowMenu(row: any): boolean {
    // Same shape as the change order list: reminder for submitted (creator),
    // delete for drafts
    return (this.isCreator(row) && row?.status === 'submitted') || row?.status === 'draft';
  }

  deleteRequest(row: any, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete change request?',
        `Are you sure you want to delete change request <b>${row.request_no || ''}</b>?`,
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.dispatch(DeleteCIWRRequest({
          payload: { id: row.id, projectId: this.projectId },
        }));
      }
    });
  }

  sendReminder(row: any, event: Event): void {
    event.stopPropagation();
    this.store.dispatch(SendChangeInWorkRequestReminderRequest({ payload: { id: row.id } }));
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
